import { ReactNode } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, LucideIcon } from "lucide-react";

interface RelatedTool {
  title: string;
  href: string;
  description: string;
}

interface ToolPageLayoutProps {
  title: string;
  description: string;
  metaDescription: string;
  icon: LucideIcon;
  slug: string;
  children: ReactNode;
  formula: {
    equation: string;
    variables: { name: string; description: string }[];
  };
  example: {
    inputs: { label: string; value: string }[];
    output: string;
    interpretation: string;
  };
  improvements: string[];
  relatedTools: RelatedTool[];
  relatedService?: { title: string; href: string };
}

const ToolPageLayout = ({
  title,
  description,
  metaDescription,
  icon: Icon,
  slug,
  children,
  formula,
  example,
  improvements,
  relatedTools,
  relatedService,
}: ToolPageLayoutProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": title,
    "description": metaDescription,
    "url": `https://xperfomax.com/tools/${slug}`,
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "provider": {
      "@type": "Organization",
      "name": "XperfoMax",
      "url": "https://xperfomax.com"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={title}
        description={metaDescription}
        canonicalUrl={`https://xperfomax.com/tools/${slug}`}
        schema={schema}
      />
      <Header />
      <main className="pt-16 md:pt-20 lg:pt-24 pb-10 md:pb-14 lg:pb-16">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Back Link */}
          <Link 
            to="/tools" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            All Marketing Tools
          </Link>

          {/* Hero */}
          <div className="mb-6 md:mb-10 lg:mb-12">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon className="w-7 h-7 text-primary" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                {title}
              </h1>
            </div>
            <p className="text-lg text-muted-foreground max-w-2xl">
              {description}
            </p>
          </div>

          {/* Tool Interface */}
          <div className="mb-8 md:mb-12 lg:mb-16">
            {children}
          </div>

          {/* What This Tool Measures */}
          <section className="mb-6 md:mb-10 lg:mb-12">
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              What This Tool Measures
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {metaDescription}
            </p>
          </section>

          {/* Formula */}
          <section className="mb-6 md:mb-10 lg:mb-12">
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              Formula
            </h2>
            <Card>
              <CardContent className="pt-6">
                <div className="bg-muted p-4 rounded-lg font-mono text-center text-lg mb-6">
                  {formula.equation}
                </div>
                <div className="space-y-3">
                  {formula.variables.map((v) => (
                    <div key={v.name} className="flex gap-2 text-sm">
                      <span className="font-medium text-foreground min-w-[120px]">{v.name}:</span>
                      <span className="text-muted-foreground">{v.description}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Practical Example */}
          <section className="mb-6 md:mb-10 lg:mb-12">
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              Practical Example
            </h2>
            <Card>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium text-foreground mb-3">Inputs:</p>
                    <ul className="space-y-2">
                      {example.inputs.map((input) => (
                        <li key={input.label} className="text-sm text-muted-foreground">
                          <span className="text-foreground">{input.label}:</span> {input.value}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground mb-3">Result:</p>
                    <p className="text-2xl font-bold text-primary mb-3">{example.output}</p>
                    <p className="text-sm text-muted-foreground">{example.interpretation}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* How to Improve */}
          <section className="mb-6 md:mb-10 lg:mb-12">
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              How to Improve This Metric
            </h2>
            <Card>
              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {improvements.map((tip, i) => (
                    <li key={i} className="flex gap-3 text-sm">
                      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-muted-foreground">{tip}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </section>

          {/* Related Tools */}
          <section className="mb-6 md:mb-10 lg:mb-12">
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              Related Tools
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {relatedTools.map((tool) => (
                <Link key={tool.href} to={tool.href}>
                  <Card className="h-full hover:border-primary/50 transition-colors">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center justify-between">
                        {tool.title}
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{tool.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>

          {/* Soft CTA */}
          <section className="border-t border-border pt-6 md:pt-10 lg:pt-12">
            <div className="text-center max-w-xl mx-auto">
              <p className="text-muted-foreground mb-4">
                Want help improving this metric across your growth system?
              </p>
              <Link 
                to="/contact" 
                className="text-primary hover:text-primary/80 font-medium inline-flex items-center gap-2"
              >
                Talk to the XperfoMax team
                <ArrowRight className="w-4 h-4" />
              </Link>
              {relatedService && (
                <p className="text-sm text-muted-foreground mt-6">
                  Or explore our{" "}
                  <Link to={relatedService.href} className="text-primary hover:underline">
                    {relatedService.title}
                  </Link>
                  {" "}capabilities.
                </p>
              )}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ToolPageLayout;
